import React, { useMemo, useState } from 'react';
import './AskUserCard.css';
import { AskUserAnswer, AskUserCardData, AskUserOption, isAnswerEmpty } from '../../helpers/askUser';

interface AskUserCardProps {
  card: AskUserCardData;
  /** False in shared views, while the reply is streaming, or with no handler. */
  canAnswer: boolean;
  /** Resolves true when the answer was accepted and the reply is resuming. */
  onSubmit?: (answers: AskUserAnswer[]) => Promise<boolean>;
  /** Shown in place of the message input: offers Skip to bring the input back. */
  onSkip?: () => void;
}

/**
 * An `ask_user` question card. While pending and answerable it steps through
 * the questions and sends the choices; once answered or passed over it
 * collapses to a read-only summary.
 */
export const AskUserCard: React.FC<AskUserCardProps> = ({ card, canAnswer, onSubmit, onSkip }) => {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState<string[][]>(() => card.questions.map(() => []));
  const [otherText, setOtherText] = useState<string[]>(() => card.questions.map(() => ''));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questionCount = card.questions.length;
  const safeStep = Math.min(step, questionCount - 1);
  const question = card.questions[safeStep];
  const isLastStep = safeStep === questionCount - 1;

  const composedAnswers = useMemo<AskUserAnswer[]>(() => card.questions.map((_, index) => {
    const typed = otherText[index]?.trim();
    return typed ? { selected: selected[index] ?? [], other: typed } : { selected: selected[index] ?? [] };
  }), [card.questions, selected, otherText]);

  const canSend = composedAnswers.some(answer => !isAnswerEmpty(answer));

  if (card.status !== 'pending') {
    return <Summary card={card} />;
  }

  const updateAt = <T,>(list: T[], index: number, value: T): T[] =>
    list.map((item, i) => (i === index ? value : item));

  const toggle = (option: AskUserOption) => {
    const current = selected[safeStep] ?? [];
    if (question.multiSelect) {
      const next = current.includes(option.label)
        ? current.filter(label => label !== option.label)
        : [...current, option.label];
      setSelected(updateAt(selected, safeStep, next));
      return;
    }
    setSelected(updateAt(selected, safeStep, [option.label]));
    setOtherText(updateAt(otherText, safeStep, ''));
    // Single choice: move straight on, but let the last one wait for Send.
    if (!isLastStep) setStep(safeStep + 1);
  };

  const changeOther = (value: string) => {
    setOtherText(updateAt(otherText, safeStep, value));
    // A single-choice question takes an option or typed text, not both.
    if (!question.multiSelect && value.trim()) {
      setSelected(updateAt(selected, safeStep, []));
    }
  };

  const submit = async (answers: AskUserAnswer[] = composedAnswers) => {
    if (!answers.some(answer => !isAnswerEmpty(answer)) || isSubmitting || !onSubmit) return;
    setIsSubmitting(true);
    const accepted = await onSubmit(answers);
    // On success the card re-renders from content as answered; only re-enable
    // after a failure so the user can try again.
    if (!accepted) setIsSubmitting(false);
  };

  const advanceOrSubmit = () => {
    if (isLastStep) void submit();
    else setStep(safeStep + 1);
  };

  /** Skips just this question. On the last one, sends whatever was answered,
   * or dismisses the card if nothing was. */
  const skipQuestion = (skipAll: () => void) => {
    setSelected(updateAt(selected, safeStep, []));
    setOtherText(updateAt(otherText, safeStep, ''));
    if (!isLastStep) {
      setStep(safeStep + 1);
      return;
    }
    const answers = updateAt(composedAnswers, safeStep, { selected: [] });
    if (answers.some(answer => !isAnswerEmpty(answer))) void submit(answers);
    else skipAll();
  };

  const disabled = !canAnswer || isSubmitting;

  return (
    <section className={`ask-user-card${onSkip ? ' is-composer' : ''}`} aria-label="Buddy has a question">
      <p className="ask-user-card__question">{question.question}</p>
      {question.multiSelect && <p className="ask-user-card__hint">Choose any that apply</p>}

      <div
        className="ask-user-card__options"
        role={question.multiSelect ? 'group' : 'radiogroup'}
        aria-label={question.question}
        key={safeStep}
      >
        {question.options.map(option => {
          const isSelected = (selected[safeStep] ?? []).includes(option.label);
          return (
            <button
              type="button"
              key={option.label}
              className={`ask-user-card__option${isSelected ? ' is-selected' : ''}`}
              role={question.multiSelect ? 'checkbox' : 'radio'}
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => toggle(option)}
            >
              <span className="ask-user-card__option-text">
                <strong>{option.label}</strong>
              </span>
            </button>
          );
        })}
        <input
          type="text"
          className="ask-user-card__other"
          placeholder="Something else…"
          value={otherText[safeStep] ?? ''}
          disabled={disabled}
          maxLength={1000}
          onChange={event => changeOther(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
              event.preventDefault();
              advanceOrSubmit();
            }
          }}
        />
      </div>

      {canAnswer ? (
        <footer className="ask-user-card__controls">
          {safeStep > 0 && (
            <button type="button" className="ask-user-card__link" onClick={() => setStep(safeStep - 1)} disabled={isSubmitting}>
              Back
            </button>
          )}
          <span className="ask-user-card__spacer" />
          {onSkip && (
            <button type="button" className="ask-user-card__link" onClick={() => skipQuestion(onSkip)} disabled={isSubmitting}>
              Skip
            </button>
          )}
          <button
            type="button"
            className="ask-user-card__primary"
            onClick={advanceOrSubmit}
            disabled={isSubmitting || (isLastStep && !canSend)}
          >
            {isSubmitting ? 'Sending…' : isLastStep ? 'Send' : 'Next'}
          </button>
        </footer>
      ) : (
        <p className="ask-user-card__hint">Waiting for an answer</p>
      )}
    </section>
  );
};

const Summary: React.FC<{ card: AskUserCardData }> = ({ card }) => {
  const describe = (index: number): string | null => {
    const answer = card.answers?.[index];
    if (card.status !== 'answered' || isAnswerEmpty(answer)) return null;
    const parts = [...answer!.selected];
    if (answer!.other) parts.push(`“${answer!.other}”`);
    return parts.join(', ');
  };

  return (
    <section className="ask-user-card is-summary" aria-label="Your answers">
      <header className="ask-user-card__header">
        <span className="ask-user-card__label">Your answers</span>
        {card.status === 'skipped' && <span className="ask-user-card__step">Skipped</span>}
      </header>
      <dl className="ask-user-card__summary">
        {card.questions.map((question, index) => {
          const answer = describe(index);
          return (
            <div key={index}>
              <dt>{question.question}</dt>
              <dd className={answer ? '' : 'is-empty'}>
                {answer ?? (card.status === 'skipped' ? 'Not answered' : 'No answer')}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
};
